import { useApolloClient } from "@apollo/client";
import { getDataOrNull, PDF_UNITS, type PdfUnit } from "@msys/common";
import {
  CardContainer,
  LoadingSpinner,
  Modal,
  ModalActions,
  ModalContent,
  ModalDialog,
  ModalOpenButton,
  ModalTitle,
  RadioGroupOption,
  useScreenWidth,
} from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Replay as ReplayIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  UploadFile as UploadIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { isEqual, omit, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { PdfVisibility } from "../../../../clients/graphqlTypes.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { AttachmentImageDragAndDropField } from "../../../commons/form-fields/AttachmentImageDragAndDropField.js";
import { ColorPickerField } from "../../../commons/form-fields/ColorPickerField.js";
import { FormattedFloatField } from "../../../commons/form-fields/FormattedFloatField.js";
import { RadioGroupField } from "../../../commons/form-fields/RadioGroupField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { FileInput } from "../../../commons/inputs/FileInput.js";
import { Attachment } from "../../attachments/helpers.js";
import {
  OrganisationPdfSettingsBox_PdfLetterheadFragment,
  useGenerateExamplePdfDocumentMutation,
  useGenerateOrganisationPdfLetterheadModalQuery,
  useGenerateOrganisationPdfLetterheadMutation,
  useCreateOrganisationPdfLetterheadMutation,
  useDeleteOrganisationPdfLetterheadMutation,
  useModifyOrganisationPdfLetterheadMutation,
  useRequestOrganisationPdfLetterheadUploadUrlMutation,
  useSetDefaultOrganisationPdfLetterheadMutation,
} from "./OrganisationPdfSettingsBox.generated.js";
import { resolveLetterheadFormInitialSettings } from "./letterheadFormHelpers.js";

// --- Margin parsing helpers (inline since @msys/pdf-utils is server-only) ---

const PREVIEW_HEIGHT = "1100px";

const MARGIN_REGEX = /^(\d+(?:\.\d+)?)(pt|mm|cm|in)$/;

const POINTS_PER_UNIT: Record<PdfUnit, number> = {
  pt: 1,
  mm: 2.83465,
  cm: 28.3465,
  in: 72,
};

function parseMargin(margin: string): { value: number; unit: PdfUnit } {
  const match = margin.match(MARGIN_REGEX);
  if (!match) {
    return { value: 0, unit: "pt" };
  }
  return { value: parseFloat(match[1]), unit: match[2] as PdfUnit };
}

function toPoints(margin: string): number {
  const parsed = parseMargin(margin);
  return parsed.value * POINTS_PER_UNIT[parsed.unit];
}

function formatMargin(value: number, unit: PdfUnit): string {
  return `${value}${unit}`;
}

// --- Composite margin field: number input + unit dropdown ---

const MarginField = ({
  valueName,
  unitName,
  label,
}: {
  valueName: string;
  unitName: string;
  label: string;
}) => {
  const { t } = useTranslate(["Quote"]);
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          flex: 2,
        }}
      >
        <FormattedFloatField name={valueName} label={label} min={0} />
      </Box>
      <Box
        sx={{
          flex: 1,
        }}
      >
        <FormControl sx={{ minWidth: 80 }}>
          <InputLabel>{t("Unit", { ns: "Quote" })}</InputLabel>
          <UnitSelect name={unitName} />
        </FormControl>
      </Box>
    </Stack>
  );
};

const UnitSelect = ({ name }: { name: string }) => {
  const { getFieldProps, setFieldValue } = useFormikContext();
  const { value } = getFieldProps<PdfUnit>(name);

  return (
    <Select
      value={value}
      onChange={async e => {
        await setFieldValue(name, e.target.value as PdfUnit);
      }}
      size="small"
      sx={{ minWidth: 70 }}
    >
      {PDF_UNITS.map(unit => (
        <MenuItem key={unit} value={unit}>
          {unit}
        </MenuItem>
      ))}
    </Select>
  );
};

interface Props {
  pdfLetterheads: OrganisationPdfSettingsBox_PdfLetterheadFragment[];
}

export function OrganisationPdfSettingsBox({ pdfLetterheads }: Props) {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);

  const defaultPdfLetterhead =
    pdfLetterheads.find(letterhead => letterhead.isDefault) ?? null;

  return (
    <CardContainer isExpandable title={t("General PDF settings")}>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          padding: 1,
        }}
      >
        {pdfLetterheads.map(pdfLetterhead => (
          <LetterheadRow
            key={pdfLetterhead.id}
            pdfLetterhead={pdfLetterhead}
            letterheadCount={pdfLetterheads.length}
          />
        ))}
        <ModalOpenButton
          Modal={CreateNewPdfLetterheadModal}
          modalProps={{ defaultPdfLetterhead }}
        >
          <Button variant="text" color="secondary" startIcon={<AddIcon />}>
            {t("Create new letterhead", { ns: "OrganisationSettings" })}
          </Button>
        </ModalOpenButton>
      </Stack>
    </CardContainer>
  );
}

const LetterheadRow = ({
  pdfLetterhead,
  letterheadCount,
}: {
  pdfLetterhead: OrganisationPdfSettingsBox_PdfLetterheadFragment;
  letterheadCount: number;
}) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const [setDefaultLetterhead] = useSetDefaultOrganisationPdfLetterheadMutation(
    { client }
  );
  const [deleteLetterhead] = useDeleteOrganisationPdfLetterheadMutation({
    client,
  });

  // The default cannot be deleted, and the two situations need different
  // instructions: with other letterheads present another can simply be promoted,
  // but a sole letterhead has to be joined by one first. Surfaced on the control
  // rather than failing on submit.
  const deleteBlockedReason = pdfLetterhead.isDefault
    ? letterheadCount > 1
      ? t("Make another letterhead the default before deleting this one.", {
          ns: "OrganisationSettings",
        })
      : t(
          "Create another letterhead and make it the default before deleting this one.",
          { ns: "OrganisationSettings" }
        )
    : null;

  const defaultLabel = pdfLetterhead.isDefault
    ? t("Default", { ns: "OrganisationSettings" })
    : t("Set as default", { ns: "OrganisationSettings" });

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Link
        component="a"
        href={pdfLetterhead.attachment.url}
        target="_blank"
        sx={{ flexGrow: 1 }}
      >
        {pdfLetterhead.name}
      </Link>

      {pdfLetterhead.isDefault && (
        <Chip
          size="small"
          label={t("Default", { ns: "OrganisationSettings" })}
        />
      )}

      {/* Every row renders the same three controls in the same order, so a
          letterhead's actions stay where the eye already found them — the
          default one disables rather than hides what it cannot do. */}
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <Tooltip title={defaultLabel}>
          <span>
            <IconButton
              color="primary"
              size="small"
              disabled={pdfLetterhead.isDefault}
              aria-label={defaultLabel}
              onClick={async () => {
                try {
                  // No refetch: the mutation returns both affected letterheads and
                  // Apollo normalises them by id. A refetch would read from a
                  // replica that can lag this write.
                  await setDefaultLetterhead({
                    variables: { input: { pdfLetterheadId: pdfLetterhead.id } },
                  });
                } catch (error) {
                  if (error instanceof Error)
                    enqueueSnackbar(error.message, { variant: "error" });
                }
              }}
            >
              {pdfLetterhead.isDefault ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t("Edit", { ns: "Global" })}>
          <span>
            <ModalOpenButton
              Modal={EditLetterheadModal}
              modalProps={{ pdfLetterhead }}
            >
              <IconButton
                color="primary"
                size="small"
                aria-label={t("Edit", { ns: "Global" })}
              >
                <EditIcon />
              </IconButton>
            </ModalOpenButton>
          </span>
        </Tooltip>

        <Tooltip title={deleteBlockedReason ?? t("Delete", { ns: "Global" })}>
          <span>
            <ModalOpenButton
              Modal={ConfirmModal}
              modalProps={{
                title: t("Delete", { ns: "Global" }),
                text: t(
                  "This letterhead will no longer be available for new documents. Already generated documents are unaffected.",
                  { ns: "OrganisationSettings" }
                ),
                handleConfirm: async () => {
                  await deleteLetterhead({
                    variables: { input: { pdfLetterheadId: pdfLetterhead.id } },
                    // Delete returns only `ok`, so there is nothing to normalise —
                    // evicting is what removes the row without a refetch.
                    update: cache => {
                      cache.evict({
                        id: cache.identify({
                          __typename: "PdfLetterhead",
                          id: pdfLetterhead.id,
                        }),
                      });
                      cache.gc();
                    },
                  });
                },
              }}
            >
              <IconButton
                color="primary"
                size="small"
                disabled={Boolean(deleteBlockedReason)}
                aria-label={t("Delete", { ns: "Global" })}
              >
                <DeleteIcon />
              </IconButton>
            </ModalOpenButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

/**
 * Uploads a letterhead PDF to the key `requestOrganisationPdfLetterheadUploadUrl`
 * mints for the acting organisation and returns the access URL naming it.
 *
 * Shared by the create and replace paths: the backend admits only that one key
 * shape, so a second hand-rolled copy of this sequence is a rejected upload
 * waiting to happen.
 */
function useLetterheadPdfUpload() {
  const { t } = useTranslate(["Global"]);
  const client = useApolloClient();
  const [requestUploadUrl] =
    useRequestOrganisationPdfLetterheadUploadUrlMutation({ client });

  return React.useCallback(
    async (file: File): Promise<string> => {
      const response = await requestUploadUrl();
      const minted =
        response.data?.requestOrganisationPdfLetterheadUploadUrl ?? null;
      if (!minted?.uploadUrl) throw new Error("Upload URL is missing");
      if (!minted.accessUrl) throw new Error("Access URL is missing");

      const upload = await fetch(minted.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "application/pdf" },
      });
      // Translated because both call sites put it straight into a snackbar,
      // unlike the two invariants above.
      if (!upload.ok)
        throw new Error(
          t("Something went wrong. Please try again.", { ns: "Global" })
        );

      return minted.accessUrl;
    },
    [requestUploadUrl, t]
  );
}

/**
 * The edit flow's replace-PDF control, offering the same two sources the create
 * flow does. Rendered through `LetterheadSettingsForm`'s `pdfAction` slot, which
 * places it inside that form's Formik — which is how the generate path writes
 * its text colours into the live form without disturbing any other field.
 */
const ReplacePdfLetterheadAction = ({
  pdfLetterhead,
  isPending,
  onUploadingChange,
  onReplaced,
}: {
  pdfLetterhead: OrganisationPdfSettingsBox_PdfLetterheadFragment;
  /**
   * Holds both sources shut while one is in flight, keeping a single
   * replacement pending at a time: two overlapping ones settle in whatever
   * order they finish, so the older could land last and replace the newer, and
   * the first to settle would report the pending state as over while the other
   * was still running.
   */
  isPending: boolean;
  /**
   * Reported for the whole of the upload, because until it lands nothing else
   * marks the replacement as pending: no field is dirtied and the preview is
   * still the stored PDF's, so Confirm would otherwise stay live over it.
   */
  onUploadingChange: (isUploading: boolean) => void;
  onReplaced: (url: string) => void;
}) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const { setFieldValue } = useFormikContext<LetterheadSettingsFormValues>();
  const fileUploadRef = React.useRef<HTMLInputElement>(null);
  const uploadPdf = useLetterheadPdfUpload();

  return (
    <>
      <Typography variant="h4">
        {t("Replace PDF", { ns: "OrganisationSettings" })}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<UploadIcon />}
          disabled={isPending}
          onClick={() => fileUploadRef.current?.click()}
        >
          {t("Upload", { ns: "Global" })}
        </Button>
        <FileInput
          innerRef={fileUploadRef}
          multiple={false}
          accept="application/pdf"
          onStart={() => onUploadingChange(true)}
          onComplete={async file => {
            try {
              // An uploaded replacement carries no generate-step colours, so
              // the letterhead's stored ones are left alone.
              onReplaced(await uploadPdf(file));
            } catch (error) {
              if (error instanceof Error)
                enqueueSnackbar(error.message, { variant: "error" });
            } finally {
              // Cleared on both outcomes: a rejected upload must give the
              // administrator the form back, not lock it. On the success path
              // the swapped `url` has already restarted the preview, so the
              // preview gate holds Confirm from here.
              onUploadingChange(false);
            }
          }}
        />
        <ModalOpenButton
          Modal={GenerateOrganisationPdfLetterheadModal}
          modalProps={{
            existingLetterhead: pdfLetterhead,
            handleComplete: async (url, colors) => {
              // Only the two colours the generate step chose are written, so a
              // name or margin edited beforehand survives the replacement.
              await setFieldValue("textPrimaryColor", colors.textPrimaryColor);
              await setFieldValue(
                "textSecondaryColor",
                colors.textSecondaryColor
              );
              onReplaced(url);
            },
          }}
        >
          <Button variant="outlined" color="secondary" disabled={isPending}>
            {t("Generate", { ns: "Global" })}
          </Button>
        </ModalOpenButton>
      </Stack>
      <Divider />
    </>
  );
};

/** Exported for its colocated spec; the box reaches it through ModalOpenButton. */
export const EditLetterheadModal = ({
  handleClose,
  pdfLetterhead,
}: {
  handleClose: () => void;
  pdfLetterhead: OrganisationPdfSettingsBox_PdfLetterheadFragment;
}) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  /** Null until the administrator supplies a replacement; the stored PDF is kept. */
  const [replacementUrl, setReplacementUrl] = React.useState<string | null>(
    null
  );
  const [isReplacementUploading, setIsReplacementUploading] =
    React.useState(false);

  const client = useApolloClient();
  const [modifyLetterhead] = useModifyOrganisationPdfLetterheadMutation({
    client,
  });

  return (
    <ModalDialog handleClose={handleClose} maxWidth="md">
      <ModalTitle
        title={t("Edit letterhead", { ns: "OrganisationSettings" })}
        handleClose={handleClose}
      />
      <LetterheadSettingsForm
        // Swapping this re-renders the preview against the replacement without
        // remounting the form, so the margins, colours and name being edited
        // survive the swap — Formik's initialValues do not reinitialize.
        url={replacementUrl ?? pdfLetterhead.attachment.url}
        name={pdfLetterhead.name}
        {...resolveLetterheadFormInitialSettings({
          existing: pdfLetterhead.settings,
        })}
        isPdfActionPending={isReplacementUploading}
        pdfAction={
          <ReplacePdfLetterheadAction
            pdfLetterhead={pdfLetterhead}
            isPending={isReplacementUploading}
            onUploadingChange={setIsReplacementUploading}
            onReplaced={setReplacementUrl}
          />
        }
        onSubmit={async values => {
          await modifyLetterhead({
            variables: {
              input: {
                pdfLetterheadId: pdfLetterhead.id,
                // Null means "keep the current PDF", so adjusting a margin or
                // renaming does not require re-uploading the document.
                values: { ...values, pdfLetterheadUrl: replacementUrl },
              },
            },
          });
        }}
        handleComplete={handleClose}
      />
    </ModalDialog>
  );
};

/** Exported for its colocated spec; the box reaches it through ModalOpenButton. */
export const CreateNewPdfLetterheadModal = ({
  handleClose,
  defaultPdfLetterhead,
}: {
  handleClose: () => void;
  /**
   * Seeded from, never modified. "The organisation's existing letterhead" stopped
   * being unambiguous once there can be several, so the default is used — it is
   * the one resolution already selects, which preserves today's behaviour.
   */
  defaultPdfLetterhead: OrganisationPdfSettingsBox_PdfLetterheadFragment | null;
}) => {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const fileUploadRef = React.useRef<HTMLInputElement>(null);
  const [url, setUrl] = React.useState<string | null>(null);
  const [generatedColors, setGeneratedColors] = React.useState<{
    textPrimaryColor: string;
    textSecondaryColor: string;
  } | null>(null);

  const uploadPdf = useLetterheadPdfUpload();

  const client = useApolloClient();
  const [createLetterhead] = useCreateOrganisationPdfLetterheadMutation({
    client,
  });

  return (
    <ModalDialog handleClose={handleClose} maxWidth={url ? "md" : "sm"}>
      <ModalTitle
        title={t("Create new letterhead", { ns: "OrganisationSettings" })}
        handleClose={handleClose}
      />
      {url ? (
        <LetterheadSettingsForm
          url={url}
          {...resolveLetterheadFormInitialSettings({
            existing: defaultPdfLetterhead?.settings,
            generated: generatedColors,
          })}
          onSubmit={async values => {
            await createLetterhead({
              variables: { input: { pdfLetterheadUrl: url, ...values } },
            });
          }}
          handleComplete={handleClose}
        />
      ) : (
        <ModalContent>
          <Stack direction={"column"} spacing={1}>
            <Button
              color="secondary"
              onClick={() => fileUploadRef.current?.click()}
            >
              {t("Upload", { ns: "Global" })}
            </Button>
            <FileInput
              innerRef={fileUploadRef}
              multiple={false}
              accept="application/pdf"
              onComplete={async file => {
                try {
                  const accessUrl = await uploadPdf(file);
                  // An uploaded letterhead has no generate-step colors;
                  // clear any stale ones so they aren't applied to it.
                  setGeneratedColors(null);
                  setUrl(accessUrl);
                } catch (error) {
                  if (error instanceof Error)
                    enqueueSnackbar(error.message, { variant: "error" });
                }
              }}
            />

            <Divider>
              <Typography sx={{ color: theme => theme.palette.grey[400] }}>
                {t("or", { ns: "Global" })}
              </Typography>
            </Divider>

            <ModalOpenButton
              Modal={GenerateOrganisationPdfLetterheadModal}
              modalProps={{
                existingLetterhead: defaultPdfLetterhead,
                handleComplete: async (url, colors) => {
                  // Set the colors before the URL: the URL turning truthy
                  // mounts LetterheadSettingsForm, whose Formik initialValues
                  // do not reinitialize, so the colors must already be set.
                  setGeneratedColors(colors);
                  setUrl(url);
                },
              }}
            >
              <Button variant="text" color="secondary">
                {t("Generate", { ns: "Global" })}
              </Button>
            </ModalOpenButton>
          </Stack>
        </ModalContent>
      )}
    </ModalDialog>
  );
};

interface LetterheadSettingsFormValues {
  name: string;
  marginTopFirstPageValue: number;
  marginTopFirstPageUnit: PdfUnit;
  marginTopSubsequentPageValue: number;
  marginTopSubsequentPageUnit: PdfUnit;
  marginBottomValue: number;
  marginBottomUnit: PdfUnit;
  marginLeftValue: number;
  marginLeftUnit: PdfUnit;
  marginRightValue: number;
  marginRightUnit: PdfUnit;
  primaryColor: string;
  secondaryColor: string;
  textPrimaryColor: string;
  textSecondaryColor: string;
}

function getMarginValues(
  margin: string | undefined,
  defaultValue: number,
  defaultUnit: PdfUnit
): { value: number; unit: PdfUnit } {
  if (!margin) return { value: defaultValue, unit: defaultUnit };
  const parsed = parseMargin(margin);
  return { value: parsed.value, unit: parsed.unit };
}

function formValuesToSubmitValues(values: LetterheadSettingsFormValues) {
  return {
    marginTopFirstPage: formatMargin(
      values.marginTopFirstPageValue,
      values.marginTopFirstPageUnit
    ),
    marginTopSubsequentPage: formatMargin(
      values.marginTopSubsequentPageValue,
      values.marginTopSubsequentPageUnit
    ),
    marginBottom: formatMargin(
      values.marginBottomValue,
      values.marginBottomUnit
    ),
    marginLeft: formatMargin(values.marginLeftValue, values.marginLeftUnit),
    marginRight: formatMargin(values.marginRightValue, values.marginRightUnit),
    primaryColor: values.primaryColor,
    secondaryColor: values.secondaryColor,
    textPrimaryColor: values.textPrimaryColor,
    textSecondaryColor: values.textSecondaryColor,
  };
}

const LetterheadSettingsForm = ({
  url,
  name,
  submitLabel,
  pdfAction,
  isPdfActionPending = false,
  onSubmit,
  marginTopFirstPage,
  marginTopSubsequentPage,
  marginBottom,
  marginLeft,
  marginRight,
  primaryColor,
  secondaryColor,
  textPrimaryColor,
  textSecondaryColor,
  handleComplete,
}: {
  url: string;
  name?: string | undefined;
  submitLabel?: string | undefined;
  /**
   * A control acting on the letterhead PDF itself, rendered above the fields.
   * The edit path passes its replace-PDF action here; the create path has just
   * chosen a PDF and passes nothing.
   */
  pdfAction?: React.ReactNode | undefined;
  /**
   * Whether `pdfAction` is part-way through supplying a new PDF. Gates Confirm,
   * because until it finishes `url` still names the document being replaced and
   * saving would keep that one while discarding the upload.
   */
  isPdfActionPending?: boolean | undefined;
  /**
   * What this form saves to. Create and edit pass different mutations while
   * sharing every field and validation rule here, which is what stops the two
   * paths diverging — both submitting one mutation is what made editing append
   * a row in the first place.
   */
  onSubmit: (
    values: { name: string } & ReturnType<typeof formValuesToSubmitValues>
  ) => Promise<void>;
  marginTopFirstPage?: string | undefined;
  marginTopSubsequentPage?: string | undefined;
  marginBottom?: string | undefined;
  marginLeft?: string | undefined;
  marginRight?: string | undefined;
  primaryColor?: string | undefined;
  secondaryColor?: string | undefined;
  textPrimaryColor?: string | undefined;
  textSecondaryColor?: string | undefined;
  handleComplete: () => void;
}) => {
  const { isMaxPhone } = useScreenWidth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate([
    "OrganisationSettings",
    "OrganisationBranding",
    "Global",
  ]);

  /**
   * The example document the pane is showing, tagged with the generation that
   * produced it — which is how a load event arriving from a pane the next
   * generation has already superseded is told apart from the current one's.
   */
  const [preview, setPreview] = React.useState<{
    url: string;
    requestId: number;
  } | null>(null);
  /**
   * Covers both halves of the wait — generating the example document, then the
   * iframe fetching it — so the pane never shows a blank frame. Every path that
   * starts a generation sets it; only the current pane's own load event clears
   * it. It gates Confirm too, which is what keeps a replaced PDF from being
   * saved before it has been shown.
   */
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(true);
  /** Two generations can be in flight at once; only the newest may write. */
  const previewRequestRef = React.useRef(0);

  const client = useApolloClient();
  const [generateExampleDocument] = useGenerateExamplePdfDocumentMutation({
    client,
  });

  const initialValues = React.useMemo((): LetterheadSettingsFormValues => {
    const topFirst = getMarginValues(marginTopFirstPage, 50, "mm");
    const topSubsequent = getMarginValues(marginTopSubsequentPage, 20, "mm");
    const bottom = getMarginValues(marginBottom, 26, "mm");
    const left = getMarginValues(marginLeft, 20, "mm");
    const right = getMarginValues(marginRight, 10, "mm");

    return {
      name: name ?? "",
      marginTopFirstPageValue: topFirst.value,
      marginTopFirstPageUnit: topFirst.unit,
      marginTopSubsequentPageValue: topSubsequent.value,
      marginTopSubsequentPageUnit: topSubsequent.unit,
      marginBottomValue: bottom.value,
      marginBottomUnit: bottom.unit,
      marginLeftValue: left.value,
      marginLeftUnit: left.unit,
      marginRightValue: right.value,
      marginRightUnit: right.unit,
      primaryColor: primaryColor ?? "#001e64",
      secondaryColor: secondaryColor ?? "#377dc3",
      textPrimaryColor: textPrimaryColor ?? "#000000",
      textSecondaryColor: textSecondaryColor ?? "#000000",
    };
  }, [
    name,
    marginBottom,
    marginLeft,
    marginRight,
    marginTopFirstPage,
    marginTopSubsequentPage,
    primaryColor,
    secondaryColor,
    textPrimaryColor,
    textSecondaryColor,
  ]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t("A name is required.", { ns: "OrganisationSettings" })),
    marginTopFirstPageValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Top margin on first page", { ns: "OrganisationSettings" }) +
          " must be at most 500pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginTopFirstPageUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 500;
        }
      )
      .label(t("Top margin on first page", { ns: "OrganisationSettings" })),
    marginTopFirstPageUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    marginTopSubsequentPageValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Top margin on subsequent pages", { ns: "OrganisationSettings" }) +
          " must be at most 350pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginTopSubsequentPageUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 350;
        }
      )
      .label(
        t("Top margin on subsequent pages", { ns: "OrganisationSettings" })
      ),
    marginTopSubsequentPageUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    marginBottomValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Bottom margin", { ns: "OrganisationSettings" }) +
          " must be at most 200pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginBottomUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 200;
        }
      )
      .label(t("Bottom margin", { ns: "OrganisationSettings" })),
    marginBottomUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    marginLeftValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Left margin", { ns: "OrganisationSettings" }) +
          " must be at most 200pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginLeftUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 200;
        }
      )
      .label(t("Left margin", { ns: "OrganisationSettings" })),
    marginLeftUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    marginRightValue: Yup.number()
      .required()
      .min(0)
      .test(
        "max-points",
        t("Right margin", { ns: "OrganisationSettings" }) +
          " must be at most 200pt",
        function (value) {
          if (value == null) return true;
          const unit = this.parent.marginRightUnit as PdfUnit;
          const pts = value * POINTS_PER_UNIT[unit];
          return pts <= 200;
        }
      )
      .label(t("Right margin", { ns: "OrganisationSettings" })),
    marginRightUnit: Yup.string()
      .required()
      .oneOf([...PDF_UNITS]),
    primaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Primary color", { ns: "OrganisationSettings" })),
    secondaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Secondary color", { ns: "OrganisationSettings" })),
    textPrimaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Text primary color", { ns: "OrganisationSettings" })),
    textSecondaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Text secondary color", { ns: "OrganisationSettings" })),
  });

  const initialSubmitValues = React.useMemo(
    () => formValuesToSubmitValues(initialValues),
    [initialValues]
  );

  React.useEffect(() => {
    const requestId = ++previewRequestRef.current;
    setIsPreviewLoading(true);
    generateExampleDocument({
      variables: {
        input: {
          pdfLetterheadUrl: url,
          marginTopFirstPage: initialSubmitValues.marginTopFirstPage,
          marginTopSubsequentPage: initialSubmitValues.marginTopSubsequentPage,
          marginBottom: initialSubmitValues.marginBottom,
          marginLeft: initialSubmitValues.marginLeft,
          marginRight: initialSubmitValues.marginRight,
          textPrimaryColor: initialSubmitValues.textPrimaryColor,
          textSecondaryColor: initialSubmitValues.textSecondaryColor,
          primaryColor: initialSubmitValues.primaryColor,
          secondaryColor: initialSubmitValues.secondaryColor,
        },
      },
    })
      .then(({ data }) => {
        if (requestId !== previewRequestRef.current) return;
        const previewUrl = data?.generateExamplePdfDocument.url ?? null;
        setPreview(previewUrl ? { url: previewUrl, requestId } : null);
        // Nothing will load, so nothing will clear the flag on our behalf.
        if (!previewUrl) setIsPreviewLoading(false);
      })
      .catch((error: unknown) => {
        if (requestId !== previewRequestRef.current) return;
        // Dropping the superseded document keeps the pane from passing off the
        // previous generation's PDF as this one's.
        setPreview(null);
        setIsPreviewLoading(false);
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
      });
  }, [enqueueSnackbar, generateExampleDocument, initialSubmitValues, url]);

  const handleSubmit = async (
    values: LetterheadSettingsFormValues,
    formikHelpers: FormikHelpers<LetterheadSettingsFormValues>
  ) => {
    const submitValues = formValuesToSubmitValues(values);
    const requestId = ++previewRequestRef.current;
    setIsPreviewLoading(true);
    generateExampleDocument({
      variables: {
        input: {
          pdfLetterheadUrl: url,
          marginTopFirstPage: submitValues.marginTopFirstPage,
          marginTopSubsequentPage: submitValues.marginTopSubsequentPage,
          marginBottom: submitValues.marginBottom,
          marginLeft: submitValues.marginLeft,
          marginRight: submitValues.marginRight,
          textPrimaryColor: submitValues.textPrimaryColor,
          textSecondaryColor: submitValues.textSecondaryColor,
          primaryColor: submitValues.primaryColor,
          secondaryColor: submitValues.secondaryColor,
        },
      },
    })
      .then(({ data }) => {
        // Gates `resetForm` too: a superseded response must not move Formik's
        // baseline onto values the pane is no longer showing.
        if (requestId !== previewRequestRef.current) return;
        const previewUrl = data?.generateExamplePdfDocument.url ?? null;
        setPreview(previewUrl ? { url: previewUrl, requestId } : null);
        if (!previewUrl) setIsPreviewLoading(false);
        formikHelpers.resetForm({ values });
      })
      .catch((error: unknown) => {
        if (requestId !== previewRequestRef.current) return;
        setPreview(null);
        setIsPreviewLoading(false);
        if (error instanceof Error)
          enqueueSnackbar(error.message, { variant: "error" });
      });
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<LetterheadSettingsFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      // Without this the create flow starts with `errors` empty and so counts as
      // valid, leaving Confirm enabled over an empty name — and Confirm's click
      // handler runs independently of Formik's submit, so it would send the
      // mutation and rely on the server to reject it.
      validateOnMount
    >
      {formikProps => {
        // The rendered preview depends on the margins and colours only, so a
        // name-only edit must not force a re-render before saving. Compared
        // against Formik's own baseline rather than the memo above, because
        // "Update preview" calls resetForm and moves that baseline.
        const settingsDirty = !isEqual(
          omit(formikProps.values, "name"),
          omit(formikProps.initialValues, "name")
        );
        // A generation that fails leaves whatever the previous one produced on
        // screen, so "not loading" is not the same as "showing the current
        // document". Only a preview tagged with the newest generation is.
        const previewIsCurrent =
          preview !== null && preview.requestId === previewRequestRef.current;

        return (
          <>
            <ModalContent>
              <Form id={formId}>
                <Stack
                  direction={isMaxPhone ? "column-reverse" : "row"}
                  spacing={1}
                >
                  {/*
                   * Rendered whether or not there is a preview yet, so the
                   * fields keep their place instead of sitting alone on the
                   * left until the first example document arrives.
                   */}
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      minWidth: 0,
                      height: PREVIEW_HEIGHT,
                    }}
                  >
                    {preview && (
                      <iframe
                        title={t("Preview", { ns: "Global" })}
                        style={{
                          border: "none",
                          width: "100%",
                          height: "100%",
                        }}
                        src={preview.url}
                        // A pane superseded while it was still fetching says
                        // nothing about the generation that replaced it, so its
                        // load must not end that one's wait.
                        onLoad={() => {
                          if (preview.requestId === previewRequestRef.current)
                            setIsPreviewLoading(false);
                        }}
                      />
                    )}
                    {isPreviewLoading && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "background.paper",
                        }}
                      >
                        <LoadingSpinner />
                      </Box>
                    )}
                  </Box>
                  <Divider
                    orientation={isMaxPhone ? "horizontal" : "vertical"}
                    flexItem
                  />
                  <Stack
                    direction="column"
                    spacing={1}
                    sx={{
                      minWidth: 300,
                    }}
                  >
                    {pdfAction}
                    <TextField
                      name="name"
                      label={t("Name", { ns: "Global" })}
                      required
                    />
                    <Divider />
                    <Typography variant="h4">
                      {t("Content settings", { ns: "OrganisationSettings" })}
                    </Typography>
                    <MarginField
                      valueName="marginTopFirstPageValue"
                      unitName="marginTopFirstPageUnit"
                      label={t("Top margin on first page", {
                        ns: "OrganisationSettings",
                      })}
                    />
                    <MarginField
                      valueName="marginTopSubsequentPageValue"
                      unitName="marginTopSubsequentPageUnit"
                      label={t("Top margin on subsequent pages", {
                        ns: "OrganisationSettings",
                      })}
                    />
                    <MarginField
                      valueName="marginBottomValue"
                      unitName="marginBottomUnit"
                      label={t("Bottom margin", { ns: "OrganisationSettings" })}
                    />
                    <MarginField
                      valueName="marginLeftValue"
                      unitName="marginLeftUnit"
                      label={t("Left margin", { ns: "OrganisationSettings" })}
                    />
                    <MarginField
                      valueName="marginRightValue"
                      unitName="marginRightUnit"
                      label={t("Right margin", { ns: "OrganisationSettings" })}
                    />
                    <Divider />
                    <ColorPickerField
                      label={t("Primary color", {
                        ns: "OrganisationSettings",
                      })}
                      name="primaryColor"
                      required
                    />
                    <ColorPickerField
                      label={t("Secondary color", {
                        ns: "OrganisationSettings",
                      })}
                      name="secondaryColor"
                      required
                    />
                    <Divider />
                    <ColorPickerField
                      label={t("Text primary color", {
                        ns: "OrganisationSettings",
                      })}
                      name="textPrimaryColor"
                      required
                    />
                    <ColorPickerField
                      label={t("Text secondary color", {
                        ns: "OrganisationSettings",
                      })}
                      name="textSecondaryColor"
                      required
                    />
                    <Button
                      variant="outlined"
                      startIcon={<ReplayIcon />}
                      type="submit"
                      disabled={!settingsDirty || !formikProps.isValid}
                    >
                      {t("Update preview", { ns: "OrganisationSettings" })}
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            </ModalContent>
            <ModalActions
              actionButtons={[
                {
                  label: submitLabel ?? t("Confirm", { ns: "Global" }),
                  buttonProps: {
                    type: "submit",
                    form: formId,
                    // Unapplied setting changes still block saving, so what is
                    // saved is always what the preview showed. An invalid or
                    // empty name blocks it too. A pending or failed preview
                    // blocks it as well: a replaced PDF changes no field, so
                    // dirtiness alone would leave Confirm live over a document
                    // that was never shown — as would an upload still in
                    // flight, which has not yet reached the preview either.
                    disabled:
                      settingsDirty ||
                      !formikProps.isValid ||
                      isPdfActionPending ||
                      isPreviewLoading ||
                      !previewIsCurrent,
                  },
                  handleClick: async () => {
                    try {
                      const submitValues = formValuesToSubmitValues(
                        formikProps.values
                      );
                      await onSubmit({
                        name: formikProps.values.name.trim(),
                        ...submitValues,
                      });
                      handleComplete();
                    } catch (error) {
                      if (error instanceof Error)
                        enqueueSnackbar(error.message, { variant: "error" });
                    }
                  },
                },
              ]}
            />
          </>
        );
      }}
    </Formik>
  );
};

interface FormValues {
  pdfFooterColumn1: string;
  pdfFooterColumn2: string;
  pdfFooterColumn3: string;
  pdfFooterVisibility: PdfVisibility;
  pdfLogo: Attachment | null;
  pdfTextPrimaryColor: string;
  pdfTextSecondaryColor: string;
}

const GenerateOrganisationPdfLetterheadModal = ({
  handleClose,
  handleComplete,
  existingLetterhead,
}: {
  handleClose: () => void;
  handleComplete: (
    url: string,
    colors: { textPrimaryColor: string; textSecondaryColor: string }
  ) => Promise<void>;
  existingLetterhead: OrganisationPdfSettingsBox_PdfLetterheadFragment | null;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate([
    "OrganisationSettings",
    "OrganisationBranding",
    "Global",
  ]);

  const client = useApolloClient();
  const query = useGenerateOrganisationPdfLetterheadModalQuery({ client });
  const organisationPdfSettings = getDataOrNull(
    query.data?.organisationPdfSettings
  );
  const [generateLetterhead] = useGenerateOrganisationPdfLetterheadMutation({
    client,
  });

  const initialValues = React.useMemo((): FormValues => {
    if (organisationPdfSettings) {
      return {
        pdfFooterColumn1: organisationPdfSettings.pdfFooterColumn1,
        pdfFooterColumn2: organisationPdfSettings.pdfFooterColumn2,
        pdfFooterColumn3: organisationPdfSettings.pdfFooterColumn3,
        pdfFooterVisibility: organisationPdfSettings.pdfFooterVisibility,
        // Prefill text colors from the existing letterhead when one exists
        // (its stored colors are the active ones used in rendered PDFs),
        // falling back to the organisation settings. Logo and footer are not
        // part of the letterhead record, so they stay sourced from settings.
        pdfTextPrimaryColor:
          existingLetterhead?.settings.textPrimaryColor ??
          organisationPdfSettings.pdfTextPrimaryColor,
        pdfTextSecondaryColor:
          existingLetterhead?.settings.textSecondaryColor ??
          organisationPdfSettings.pdfTextSecondaryColor,
        pdfLogo: organisationPdfSettings.pdfLogo ?? null,
      };
    }

    return {
      pdfFooterColumn1: "",
      pdfFooterColumn2: "",
      pdfFooterColumn3: "",
      pdfFooterVisibility: "no",
      pdfLogo: null,
      pdfTextPrimaryColor: "",
      pdfTextSecondaryColor: "",
    };
  }, [organisationPdfSettings, existingLetterhead]);

  const validationSchema = Yup.object().shape({
    pdfFooterColumn1: Yup.string().label(
      t("Column A", { ns: "OrganisationSettings" })
    ),
    pdfFooterColumn2: Yup.string().label(
      t("Column B", { ns: "OrganisationSettings" })
    ),
    pdfFooterColumn3: Yup.string().label(
      t("Column C", { ns: "OrganisationSettings" })
    ),
    pdfTextPrimaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Text primary color", { ns: "OrganisationSettings" })),
    pdfTextSecondaryColor: Yup.string()
      .required()
      .matches(
        /^#[a-f\d]{6}$/i,
        t("Wrong color format, only HEX is allowed", {
          ns: "OrganisationBranding",
        })
      )
      .label(t("Text secondary color", { ns: "OrganisationSettings" })),
    pdfFooterVisibility: Yup.string().required(),
    pdfLogo: Yup.object().nullable(),
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const input = {
        pdfFooterColumn1: values.pdfFooterColumn1,
        pdfFooterColumn2: values.pdfFooterColumn2,
        pdfFooterColumn3: values.pdfFooterColumn3,
        pdfFooterVisibility: values.pdfFooterVisibility,
        pdfTextPrimaryColor: values.pdfTextPrimaryColor,
        pdfTextSecondaryColor: values.pdfTextSecondaryColor,
        pdfLogo: values.pdfLogo
          ? omit(values.pdfLogo, "id", "__typename")
          : null,
      };
      const { data } = await generateLetterhead({
        variables: {
          input,
        },
      });
      const url = data?.generateOrganisationPdfLetterhead.url;
      if (!url) throw new Error("URL is missing");
      await handleComplete(url, {
        textPrimaryColor: values.pdfTextPrimaryColor,
        textSecondaryColor: values.pdfTextSecondaryColor,
      });
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  const formId = React.useMemo(() => uniqueId(), []);

  if (query.loading) return null;

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      <Form id={formId}>
        <Modal
          title={t("Generate PDF letterhead", { ns: "OrganisationSettings" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Generate", { ns: "Global" }),
              buttonProps: { type: "submit", form: formId },
            },
          ]}
          isLoading={query.loading}
        >
          <Stack direction="column" spacing={1}>
            <AttachmentImageDragAndDropField
              label={t("Logo in header", { ns: "OrganisationSettings" })}
              name="pdfLogo"
            />

            <Divider />
            <Typography>
              {t("Page colors", { ns: "OrganisationSettings" })}
            </Typography>

            <ColorPickerField
              label={t("Text primary color", { ns: "OrganisationSettings" })}
              name="pdfTextPrimaryColor"
              required
            />
            <ColorPickerField
              label={t("Text secondary color", { ns: "OrganisationSettings" })}
              name="pdfTextSecondaryColor"
              required
            />

            <Divider />

            <Typography>
              {t("Page footer", { ns: "OrganisationSettings" })}
            </Typography>
            <PdfVisibilityField name="pdfFooterVisibility" />

            <TextField
              name="pdfFooterColumn1"
              label={t("Column A", { ns: "OrganisationSettings" })}
              multiline
              disabled={false}
              rows={3}
              helperText={t(
                "Eg. address, contact phone, contact email address etc.",
                { ns: "OrganisationSettings" }
              )}
            />

            <TextField
              name="pdfFooterColumn2"
              label={t("Column B", { ns: "OrganisationSettings" })}
              multiline
              disabled={false}
              rows={3}
              helperText={t(
                "Eg. tax ID, registration office, managing director etc.",
                { ns: "OrganisationSettings" }
              )}
            />

            <TextField
              name="pdfFooterColumn3"
              label={t("Column C", { ns: "OrganisationSettings" })}
              multiline
              disabled={false}
              rows={3}
              helperText={t("Eg. bank account, bank name, BIC code etc.", {
                ns: "OrganisationSettings",
              })}
            />
          </Stack>
        </Modal>
      </Form>
    </Formik>
  );
};

const usePdfVisibilityTranslation = () => {
  const { t } = useTranslate("PdfVisibilityField");

  return (pdfVisibility: PdfVisibility) => {
    switch (pdfVisibility) {
      case "all_pages":
        return t("On all pages");
      case "first_page":
        return t("Only on the first page");
      case "no":
        return t("None");
    }
  };
};

const ALL_PDF_VISIBILITY_TYPES: PdfVisibility[] = [
  "all_pages",
  "first_page",
  "no",
];

const PdfVisibilityField = (props: { name: string }) => {
  const t = usePdfVisibilityTranslation();
  const options: RadioGroupOption[] = ALL_PDF_VISIBILITY_TYPES.map(
    pdfVisibility => ({
      label: t(pdfVisibility), // FIXME: don't call t with variable, scanner will not detect these values
      value: pdfVisibility,
    })
  );
  return <RadioGroupField {...props} options={options} condensed />;
};
